import React, { useEffect, useRef, useState } from 'react'
import { FiMenu, FiX } from 'react-icons/fi'
import AnimatedCtaLink from '../AnimatedCtaLink'
import AnimatedBrandMark from '../AnimatedBrandMark'
import { APP_ENTRY_HREF, APP_PAGE_ANCHOR } from '../../constants/navigation'
import './navbar.css'

const navLinks = [
  { id: 'home', label: 'Accueil', sublabel: 'Home', href: '#home' },
  { id: 'about', label: 'À propos', sublabel: 'About', href: '#about' },
  { id: 'scenes', label: 'Scènes', sublabel: 'Scenes', href: '#scenes' },
  { id: 'use-case', label: "Cas d'Usage", sublabel: 'Use Case', href: '#use-case' },
  { id: 'resources', label: 'Conseils', sublabel: 'Tips', href: '#resources' },
]

const Navbar = ({ activePage = 'home', onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [clickedItem, setClickedItem] = useState(null)
  const navbarRef = useRef(null)
  const menuButtonRef = useRef(null)
  const interactionTimer = useRef(null)
  const interactionFrame = useRef(null)

  useEffect(() => () => {
    window.clearTimeout(interactionTimer.current)
    window.cancelAnimationFrame(interactionFrame.current)
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [activePage])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return
      setIsOpen(false)
      menuButtonRef.current?.focus()
    }

    const handlePointerDown = (event) => {
      if (!navbarRef.current?.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [isOpen])

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined

    const mobileQuery = window.matchMedia('(max-width: 900px)')
    const handleMobileChange = (event) => {
      if (!event.matches) setIsOpen(false)
    }

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', handleMobileChange)
      return () => mobileQuery.removeEventListener('change', handleMobileChange)
    }

    mobileQuery.addListener?.(handleMobileChange)
    return () => mobileQuery.removeListener?.(handleMobileChange)
  }, [])

  const showClickFeedback = (item) => {
    window.clearTimeout(interactionTimer.current)
    window.cancelAnimationFrame(interactionFrame.current)
    setClickedItem(null)

    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (reduceMotion) return

    interactionFrame.current = window.requestAnimationFrame(() => {
      setClickedItem(item)
      interactionTimer.current = window.setTimeout(() => setClickedItem(null), 560)
    })
  }

  const handlePageNavigation = (event, page, feedbackItem = page) => {
    setIsOpen(false)
    showClickFeedback(feedbackItem)

    if (!onNavigate || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return
    }

    event.preventDefault()
    onNavigate(page)
  }

  const handleAppNavigation = () => {
    setIsOpen(false)
    onNavigate?.(APP_PAGE_ANCHOR)
  }

  return (
    <header className={`navbar${isOpen ? ' navbar--menu-open' : ''}`} ref={navbarRef}>
      <a
        className={`navbar__brand octo-brand-owner${clickedItem === 'brand' ? ' navbar__brand--activating octo-brand-owner--activating' : ''}`}
        href="#home"
        aria-label="OctoSynapse home"
        onClick={(event) => handlePageNavigation(event, 'home', 'brand')}
      >
        <AnimatedBrandMark />
        <span className="navbar__brand-name">OctoSynapse</span>
      </a>

      <button
        className="navbar__menu-button"
        ref={menuButtonRef}
        type="button"
        aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={isOpen}
        aria-controls="primary-navigation"
        onClick={() => setIsOpen((current) => !current)}
      >
        {isOpen ? <FiX /> : <FiMenu />}
      </button>

      <nav
        className={`navbar__nav ${isOpen ? 'navbar__nav--open' : ''}`}
        id="primary-navigation"
        aria-label="Primary navigation"
      >
        <ul>
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                className={`navbar__link${activePage === link.id ? ' navbar__link--active' : ''}${clickedItem === link.id ? ' navbar__link--clicked' : ''}`}
                href={link.href}
                aria-current={activePage === link.id ? 'page' : undefined}
                onClick={(event) => handlePageNavigation(event, link.id)}
              >
                <span lang="fr">{link.label}</span>
                <small lang="en">{link.sublabel}</small>
              </a>
            </li>
          ))}
        </ul>

        <AnimatedCtaLink
          className="navbar__cta"
          variant="compact"
          href={APP_ENTRY_HREF}
          aria-current={activePage === 'app' ? 'page' : undefined}
          onNavigate={handleAppNavigation}
          label="Commencer"
          labelEn="Get started"
          activatingLabel="Connexion…"
          activatingLabelEn="Connecting…"
        />
      </nav>
    </header>
  )
}

export default Navbar
